---
title: "Przegląd techniczny budynku — krok po kroku"
description: "Obowiązkowy przegląd techniczny budynku — co obejmuje, jak go przeprowadzić, jak udokumentować i jak Pinveo przyspiesza cały proces."
date: "2026-06-06"
slug: "przeglad-techniczny-budynku-krok-po-kroku"
---

## Czym jest przegląd techniczny budynku?

Przepisy Prawa budowlanego nakładają na właścicieli i zarządców nieruchomości obowiązek przeprowadzania **okresowych kontroli stanu technicznego** obiektów budowlanych. Celem jest zapewnienie bezpieczeństwa użytkowników oraz utrzymanie budynku w należytym stanie.

Kontrole dzielą się na:

- **roczne** — ocena stanu technicznego elementów narażonych na szkodliwe wpływy atmosferyczne i niszczące działanie czynników zewnętrznych (elewacje, dachy, instalacje),
- **pięcioletnie** — pełna kontrola stanu technicznego i przydatności do użytkowania, w tym estetyki budynku,
- **doraźne** — po katastrofie budowlanej, silnych wiatrach, intensywnych opadach lub innych zjawiskach atmosferycznych.

Brak wymaganych przeglądów grozi **mandatem lub nakazem rozbiórki** ze strony nadzoru budowlanego.

---

## Zakres kontroli rocznej i pięcioletniej

### Kontrola roczna obejmuje m.in.:

- stan elementów elewacji (pęknięcia, odpryski, zawilgocenia),
- stan dachu i obróbek blacharskich,
- instalacje gazowe i przewody kominowe (spalinowe, wentylacyjne, dymowe),
- instalacje elektryczne i piorunochronne — w zakresie bezpieczeństwa pożarowego,
- urządzenia do odśnieżania i usuwania lodu.

### Kontrola pięcioletnia dodatkowo uwzględnia:

- instalacje elektryczne i piorunochronowe (szczegółowo),
- przewody instalacji sanitarnych i centralnego ogrzewania,
- stan balkonów, loggi i tarasów,
- budowle wchodzące w skład kompleksu (drogi wewnętrzne, parkingi).

Kontrolę **może przeprowadzać wyłącznie osoba z uprawnieniami budowlanymi** w odpowiedniej specjalności lub mistrz kominiarski (w zakresie kominiarskim).

---

## Jak udokumentować przegląd techniczny?

Prawidłowa dokumentacja przeglądu musi zawierać:

1. **Dane obiektu** — adres, numer działki, rok budowy, powierzchnia.
2. **Zakres kontroli** — jakie elementy zostały skontrolowane.
3. **Opis stwierdzonych usterek** — z lokalizacją i klasyfikacją wagi (nieistotna / do naprawy / pilna).
4. **Dokumentację fotograficzną** — zdjęcia z geolokalizacją i znacznikami miejsca.
5. **Zalecenia i terminy usunięcia usterek**.
6. **Podpis i pieczęć** osoby uprawnionej oraz właściciela/zarządcy.

Protokół należy przechowywać w **księdze obiektu budowlanego** przez cały czas istnienia budynku, a kopie — przez co najmniej 5 lat.

---

## Najczęstsze błędy przy dokumentowaniu

- Brak zdjęć lub zdjęcia bez geolokalizacji.
- Nieprecyzyjne opisy lokalizacji usterek („uszkodzenie dachu" zamiast „pęknięcie obróbki blacharskiej nad oknem nr 3 na poziomie 3").
- Niekompletna lista skontrolowanych elementów.
- Brak podpisów stron.
- Protokół sporządzony po terminie (kontrola roczna do końca maja każdego roku).

---

## Jak Pinveo wspiera przeglądy techniczne budynków?

Pinveo to system do dokumentacji terenowej stworzony z myślą o inspektorach, zarządcach nieruchomości i firmach budowlanych. Przy obowiązkowych przeglądach technicznych pomaga na każdym etapie:

**W terenie:**
- Formularz przeglądu dostosowany do kontroli stanu technicznego budynku — wbudowane pola dla wszystkich wymaganych elementów.
- Zdjęcia z automatyczną geolokalizacją i znacznikiem czasu — bez potrzeby ręcznego opisywania miejsca.
- Praca **offline** — brak zasięgu nie zatrzymuje pracy; dane synchronizują się po powrocie do sieci.
- Oznaczanie usterek bezpośrednio na mapie lub rzucie budynku.

**Po powrocie z terenu:**
- Automatyczne generowanie protokołu PDF z wypełnionymi polami, zdjęciami i podpisami.
- Historia wszystkich przeglądów danego obiektu w jednym miejscu.
- Powiadomienia o zbliżającym się terminie kolejnej kontroli.
- Raportowanie dla zarządu lub właściciela bez dodatkowej pracy.

Dowiedz się więcej o tym, jak Pinveo sprawdza się przy przeglądach technicznych: [Przeglądy okresowe budynków z Pinveo](/zastosowania/przeglady-okresowe-budynkow).

---

## Podsumowanie

Obowiązkowy przegląd techniczny budynku to wymóg prawny, który wymaga starannej dokumentacji. Właściwe narzędzia pozwalają skrócić czas poświęcony na papierkową robotę i skupić się na tym, co ważne — rzetelnej ocenie stanu technicznego budynku. Pinveo łączy formularz inspekcyjny, dokumentację zdjęciową i generowanie raportów w jednym, mobilnym narzędziu.
